import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { getMyData } from "../services/myData";
import { formatIfTimestamp } from "../utils/formatDate";
import { Badge } from "../components/utils/Badge";
import { Table } from "../components/utils/Table";
import { Typography } from "../components/utils/Typography";

const columns = [
  { key: "attribute", label: "Data Category" },
  {
    key: "value",
    label: "Value",
    className: "font-semibold text-gray-800 whitespace-nowrap",
    render: (row) => formatIfTimestamp(row.value),
  },
  {
    key: "consents",
    label: "Active in Consents",
    render: (row) => {
      const consentTags = (row.consents || []).flatMap(
        (c) => c.consent_names || [],
      );
      return (
        <div className="flex flex-wrap gap-2">
          {consentTags.map((name, i) => (
            <Badge key={i} value={name} variant="consentTag" />
          ))}
        </div>
      );
    },
  },
];

export default function MyData() {
  const { companyId } = useParams();
  const location = useLocation();
  const companyName =
    location.state?.companyName ||
    localStorage.getItem("tenant_name") ||
    "Company";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getMyData(companyId)
      .then((res) => setData(Array.isArray(res.data.data) ? res.data.data : []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [companyId]);

  const isEmpty = !loading && data.length === 0;

  return (
    <div className="p-4 md:p-6">
      {!isEmpty && (
        <>
          {/* Desktop heading */}
          <div className="hidden md:block mb-5">
            <Typography variant="sectionTitle">
              Data held by {companyName}
            </Typography>
            <Typography variant="body" color="muted" className="mt-0.5">
              Submit a request to make changes to your personal data
            </Typography>
          </div>

          {/* Mobile heading */}
          <div className="md:hidden mb-4">
            <Typography variant="sectionTitle">Your Data</Typography>
            <Typography variant="body" color="muted" className="mt-0.5">
              Submit a request to make changes to your personal data
            </Typography>
          </div>
        </>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">
          Loading...
        </div>
      ) : isEmpty ? (
        <div className="flex flex-col items-center justify-center text-center min-h-[75vh] px-4">
          <Typography variant="body" className="max-w-lg">
            No data to show right now, information that&apos;s no longer
            needed has been removed, though some may be retained where the
            law requires it (for example, tax or regulatory records).
          </Typography>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl overflow-hidden">
            <Table
              columns={columns}
              rows={data}
              rowKey={(row, idx) => row.attribute || idx}
            />
          </div>

          {/* Mobile cards */}
          <div className="flex flex-col gap-3 md:hidden">
            {data.map((item, idx) => {
              const consentTags = (item.consents || []).flatMap(
                (c) => c.consent_names || [],
              );
              return (
                <div
                  key={item.attribute || idx}
                  className="bg-white rounded-xl border border-gray-200 p-4"
                >
                  <div className="text-xs text-gray-400 mb-1">
                    {item.attribute}
                  </div>
                  <div className="text-sm font-semibold text-gray-800">
                    {formatIfTimestamp(item.value)}
                  </div>
                  {consentTags.length > 0 && (
                    <>
                      <div className="border-t border-gray-100 my-3" />
                      <div className="text-xs text-gray-400 mb-1.5">
                        Collected Via
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {consentTags.map((name, i) => (
                          <Badge key={i} value={name} variant="purpose" />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
